import { useEffect, useRef, useState } from 'react'
import { captchaOption } from '../../lib/turnstile'
import { LANGUAGES, type Language, intlLocale } from '@webbuilder/i18n'
import { useI18n } from '../../lib/i18n'
import type { Session, UserIdentity } from '@supabase/supabase-js'
import { supabase } from '../../lib/supabase'
import { useToast } from '../Toast'
import { suggestEmail, domainHasMx, isValidEmailFormat } from '../../lib/emailCheck'
import { cancelPendingEmailChange, getAccountDeletionPreflight, getPendingEmailChange, scheduleAccountDeletion, type AccountDeletionPreflight } from '../../lib/api'
import { rememberScheduledDeletion } from '../../lib/accountDeletionNotice'
import { compressImageForUpload } from '@webbuilder/media'
import { ProfileAvatarContent } from '@webbuilder/ui'
import { CodeInput, CODE_LENGTH } from '../CodeInput'
import { PendingChangesActions } from '../DashboardTopbarActions'
import { IconLanguage, IconCamera, IconDeviceDesktop, IconLink, IconLoader2, IconLogout, IconMail, IconMailCheck, IconTrash, IconUnlink, IconUser } from '@tabler/icons-react'
import { signOutEverywhere } from '../../lib/editorSession'
import { activeLanguage, translate, translateServerError } from '../../lib/i18n'

/** Official multicolor Google "G" logo. */
function GoogleIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  )
}

const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined) ?? 'http://localhost:8787'

type SessionInfo = { id: string; userAgent: string | null; createdAt: string; current: boolean; city: string | null; region: string | null }

/** Human label from a user-agent string. */
function deviceLabel(ua: string | null): string {
  if (!ua) return 'Dispositivo desconocido'
  const os = /iphone/i.test(ua) ? 'iPhone'
    : /ipad/i.test(ua) ? 'iPad'
    : /android/i.test(ua) ? 'Android'
    : /mac os x|macintosh/i.test(ua) ? 'Mac'
    : /windows/i.test(ua) ? 'Windows'
    : /linux/i.test(ua) ? 'Linux'
    : 'Dispositivo'
  const browser = /edg/i.test(ua) ? 'Edge'
    : /chrome|crios/i.test(ua) ? 'Chrome'
    : /firefox|fxios/i.test(ua) ? 'Firefox'
    : /safari/i.test(ua) ? 'Safari'
    : ''
  return browser ? `${browser} · ${os}` : os
}

function fmtDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(intlLocale(activeLanguage()), { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function sessionLocation(session: SessionInfo): string {
  if (session.city && session.region) return session.city === session.region ? session.city : `${session.city}, ${session.region}`
  return session.city || session.region || translate('account.locationUnknown')
}

export function WorkspaceSettings({
  session,
  projects,
  onOpenRegion,
}: {
  session: Session
  projects: { id: string; name: string }[]
  /** Abre la Región del proyecto abierto. Sin proyecto no se pasa: la cuenta
      se puede mirar desde fuera de cualquiera, y entonces no hay a dónde ir. */
  onOpenRegion?: (() => void) | undefined
}) {
  const { showToast } = useToast()
  const { t, language, setLanguage, fmt } = useI18n()
  const user = session.user
  const meta = user.user_metadata as
    | { full_name?: string; name?: string; user_name?: string; avatar_url?: string; picture?: string }
    | undefined

  const currentEmail = user.email ?? ''
  const token = session.access_token
  const identities: UserIdentity[] = user.identities ?? []
  // "Email login" = the user can sign in with a code sent to their email (has an
  // email identity). Google-only accounts manage their email from Google instead.
  const hasEmailLogin = identities.some((id) => id.provider === 'email')
  const googleIdentity = identities.find((id) => id.provider === 'google')

  // Profile
  const initialProfileName = meta?.full_name || meta?.name || meta?.user_name || ''
  const initialProfileAvatar = meta?.avatar_url || meta?.picture || ''
  const [name, setName] = useState(initialProfileName)
  const [avatar, setAvatar] = useState(initialProfileAvatar)
  const [savedName, setSavedName] = useState(initialProfileName.trim())
  const [savedAvatar, setSavedAvatar] = useState(initialProfileAvatar)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const profileChanged = name.trim() !== savedName || avatar !== savedAvatar

  // Email change (verified with a code sent to the new address)
  const [newEmail, setNewEmail] = useState(currentEmail)
  // Email change wizard: form → verify code from the CURRENT inbox (proves identity)
  // → verify code from the NEW inbox (proves ownership) → done.
  const [emailStep, setEmailStep] = useState<'form' | 'verify-current' | 'verify-new'>('form')
  // Which code step a paused change can jump back to (its code is still valid).
  const [emailResume, setEmailResume] = useState<null | 'verify-current' | 'verify-new'>(null)
  // Pending email change is driven by the backend (auth.users), gated to a 10-minute
  // window since the code was sent, so a stale change never lingers after a refresh.
  const [pendingEmail, setPendingEmail] = useState('')
  const [emailDismissed, setEmailDismissed] = useState(false)
  const [currentCode, setCurrentCode] = useState('')
  const [newCode, setNewCode] = useState('')
  const [emailLoading, setEmailLoading] = useState<null | 'start' | 'current' | 'new' | 'resend'>(null)
  const emailChanged = newEmail.trim() !== '' && newEmail.trim().toLowerCase() !== currentEmail.toLowerCase()
  const emailSuggestion = emailChanged ? suggestEmail(newEmail) : null
  const settingsChanged = profileChanged || (emailStep === 'form' && emailChanged)

  // Load the backend-gated pending change on mount, and auto-hide the banner the
  // moment the 10-minute window elapses (without needing a refresh).
  useEffect(() => {
    let alive = true
    let timer: ReturnType<typeof setTimeout> | undefined
    getPendingEmailChange(token)
      .then((data) => {
        if (!alive) return
        setPendingEmail(data.pendingEmail ?? '')
        if (data.pendingEmail && data.expiresAt) {
          const ms = new Date(data.expiresAt).getTime() - Date.now()
          if (ms > 0) timer = setTimeout(() => { if (alive) setPendingEmail('') }, ms)
        }
      })
      .catch(() => { /* no banner if we can't read it */ })
    return () => { alive = false; if (timer) clearTimeout(timer) }
  }, [token])

  // Account
  const [signingOutAll, setSigningOutAll] = useState(false)
  const [sessions, setSessions] = useState<SessionInfo[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(true)
  const [revokingId, setRevokingId] = useState<string | null>(null)
  const [deleteText, setDeleteText] = useState('')
  const [deleteStep, setDeleteStep] = useState<'idle' | 'code'>('idle')
  const [deleteCode, setDeleteCode] = useState('')
  const [deleteSending, setDeleteSending] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  /* La palabra es la que se pide en pantalla, en el idioma del panel. Estaba
     fija en «ELIMINAR» y en inglés, donde se pide «DELETE», el botón no se
     encendía nunca. */
  /* Lo que hay que resolver antes: cada dominio registrado aquí pide una
     decisión, y el resto son avisos de lo que dejará de funcionar. Ver
     `services/accountDeletion` en el worker. */
  const [preflight, setPreflight] = useState<AccountDeletionPreflight | null>(null)
  const [domainChoices, setDomainChoices] = useState<Record<string, 'transfer' | 'expire'>>({})
  useEffect(() => {
    let alive = true
    void getAccountDeletionPreflight(session.access_token)
      .then((data) => { if (alive) setPreflight(data) })
      .catch(() => { /* Sin la revisión el servidor sigue pidiendo las decisiones: no se pierde nada. */ })
    return () => { alive = false }
  }, [session.access_token])
  const domainsDecided = (preflight?.domains ?? []).every((domain) => domainChoices[domain.id])
  const deleteBlocked = !preflight || preflight.purchasePending || !domainsDecided
  const deleteReady = !deleteBlocked && deleteText.trim().toUpperCase() === t('account.delete.word').toUpperCase()

  const fallbackName = name || currentEmail
  const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback)
  const orderedSessions = [...sessions].sort((a, b) => Number(b.current) - Number(a.current))

  async function loadSessions() {
    setSessionsLoading(true)
    try {
      const res = await fetch(`${WORKER_URL}/api/account/sessions`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (!res.ok) throw new Error('fail')
      const { data } = (await res.json()) as { data: SessionInfo[] }
      setSessions(data)
    } catch {
      setSessions([])
    } finally {
      setSessionsLoading(false)
    }
  }
  useEffect(() => { void loadSessions() }, [session.access_token]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleRevokeSession(id: string) {
    setRevokingId(id)
    try {
      const res = await fetch(`${WORKER_URL}/api/account/sessions/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (!res.ok) throw new Error('fail')
      setSessions((list) => list.filter((s) => s.id !== id))
      showToast(t('account.signedOut'))
    } catch (e) {
      showToast(errText(e, t('account.err.signOutSession')), 'error')
    } finally {
      setRevokingId(null)
    }
  }

  async function handleAvatarFile(file: File) {
    if (!file.type.startsWith('image/')) { showToast(t('common.pickImage'), 'error'); return }
    if (file.size > 10 * 1024 * 1024) { showToast(t('account.photoTooBig'), 'error'); return }
    setUploading(true)
    try {
      const uploadFile = await compressImageForUpload(file)
      const fd = new FormData()
      fd.append('file', uploadFile)
      const res = await fetch(`${WORKER_URL}/api/assets/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: fd,
      })
      if (!res.ok) {
        throw new Error(t('account.err.uploadImage'))
      }
      const { data } = (await res.json()) as { data: { url: string } }
      setAvatar(data.url)
    } catch (e) {
      showToast(errText(e, t('account.err.uploadImage')), 'error')
    } finally {
      setUploading(false)
    }
  }

  async function handleSaveProfile() {
    setSaving(true)
    try {
      const { error } = await supabase.auth.updateUser({
        data: { ...(meta ?? {}), full_name: name.trim(), avatar_url: avatar },
      })
      if (error) throw error
      setSavedName(name.trim())
      setSavedAvatar(avatar)
      showToast(t('account.profile.saved'))
    } catch (e) {
      showToast(errText(e, t('account.err.save')), 'error')
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveSettings() {
    if (profileChanged) await handleSaveProfile()
    if (emailStep === 'form' && emailChanged) await handleStartEmailChange()
  }

  function handleDiscardSettings() {
    setName(savedName)
    setAvatar(savedAvatar)
    if (emailStep === 'form') setNewEmail(currentEmail)
    showToast(t('common.discarded'))
  }

  // Non-destructive "back": collapse to the form but keep the wizard resumable so the
  // already-sent (still valid) code can be reused without waiting out the resend limit.
  function backToForm() {
    setEmailStep('form')
  }

  // Full reset: used on success and when the user explicitly discards the change.
  // Also cancels the pending change server-side so it doesn't return on refresh.
  function discardEmailChange() {
    setEmailStep('form')
    setEmailResume(null)
    setEmailDismissed(true)
    setPendingEmail('')
    setCurrentCode('')
    setNewCode('')
    setNewEmail(currentEmail)
    void cancelPendingEmailChange(token).catch(() => { /* best-effort; banner already hidden */ })
  }

  // Step 1 → send a code to the CURRENT inbox to re-verify identity before we touch
  // the address (so a hijacked session alone can't start a change).
  async function handleStartEmailChange() {
    const target = newEmail.trim()
    if (!isValidEmailFormat(target)) { showToast(t('account.email.invalidFormat'), 'error'); return }
    if (target.toLowerCase() === currentEmail.toLowerCase()) { showToast(t('account.email.alreadyYours'), 'error'); return }
    setEmailDismissed(false)
    setEmailLoading('start')
    try {
      // Reject domains that can't receive mail (catches typos like "gamil.com").
      const mx = await domainHasMx(target.slice(target.lastIndexOf('@') + 1))
      if (mx === false) { showToast(t('account.email.noMx'), 'error'); return }

      const { error } = await supabase.auth.signInWithOtp({ email: currentEmail, options: { ...await captchaOption(), shouldCreateUser: false } })
      if (error) throw error
      setCurrentCode('')
      setNewCode('')
      setEmailStep('verify-current')
      setEmailResume('verify-current')
      showToast(t('account.email.sentToCurrent'))
    } catch (e) {
      showToast(errText(e, t('account.err.startChange')), 'error')
    } finally {
      setEmailLoading(null)
    }
  }

  // Step 2 → verify the current-inbox code, then request the change, which emails a
  // second code to the NEW address.
  async function handleVerifyCurrent(token: string) {
    if (emailLoading) return
    if (token.length < CODE_LENGTH) { showToast(t('account.email.writeCode'), 'error'); return }
    setEmailLoading('current')
    try {
      const { error } = await supabase.auth.verifyOtp({ email: currentEmail, token, type: 'email' })
      if (error) throw new Error(t('account.err.badCode'))

      const { error: updateError } = await supabase.auth.updateUser({ email: newEmail.trim() })
      if (updateError) {
        const msg = /already|registered|exists/i.test(updateError.message) ? t('account.err.emailInUse') : t('account.err.startChange')
        throw new Error(msg)
      }
      setNewCode('')
      setEmailStep('verify-new')
      setEmailResume('verify-new')
      showToast(t('account.email.sentToNew'))
    } catch (e) {
      showToast(errText(e, t('account.err.verifyCode')), 'error')
    } finally {
      setEmailLoading(null)
    }
  }

  // Step 4 → verify the new-inbox code, which finalizes the change.
  async function handleVerifyNew(token: string) {
    if (emailLoading) return
    if (token.length < CODE_LENGTH) { showToast(t('account.email.writeCode'), 'error'); return }
    setEmailLoading('new')
    try {
      const { error } = await supabase.auth.verifyOtp({ email: newEmail.trim(), token, type: 'email_change' })
      if (error) throw new Error(t('account.err.badCode'))
      // Security: end every other session so any stale login on the old email is cut off.
      await supabase.auth.signOut({ scope: 'others' })
      discardEmailChange()
      void loadSessions()
      showToast(t('account.email.updated'))
    } catch (e) {
      showToast(errText(e, t('account.err.confirmEmail')), 'error')
    } finally {
      setEmailLoading(null)
    }
  }

  async function handleResendCurrent() {
    setEmailLoading('resend')
    try {
      const { error } = await supabase.auth.signInWithOtp({ email: currentEmail, options: { ...await captchaOption(), shouldCreateUser: false } })
      if (error) throw error
      showToast(t('account.codeResent'))
    } catch (e) {
      showToast(errText(e, t('account.err.resendCode')), 'error')
    } finally {
      setEmailLoading(null)
    }
  }

  async function handleResendNew() {
    setEmailLoading('resend')
    try {
      const { error } = await supabase.auth.resend({ type: 'email_change', email: newEmail.trim() })
      if (error) throw error
      showToast(t('account.codeResent'))
    } catch (e) {
      showToast(errText(e, t('account.err.resendCode')), 'error')
    } finally {
      setEmailLoading(null)
    }
  }

  async function handleLinkGoogle() {
    try {
      const { error } = await supabase.auth.linkIdentity({ provider: 'google' })
      if (error) throw error
      // Redirects to Google on success.
    } catch (e) {
      showToast(errText(e, t('account.err.connectGoogle')), 'error')
    }
  }

  async function handleUnlink(identity: UserIdentity) {
    if (identities.length <= 1) { showToast(t('account.providers.lastOne'), 'error'); return }
    try {
      const { error } = await supabase.auth.unlinkIdentity(identity)
      if (error) throw error
      showToast(t('account.providers.disconnected'))
    } catch (e) {
      showToast(errText(e, t('account.err.disconnect')), 'error')
    }
  }

  async function handleSignOutAll() {
    setSigningOutAll(true)
    try {
      await signOutEverywhere({ scope: 'global' })
    } catch (e) {
      showToast(errText(e, t('account.err.signOutAll')), 'error')
      setSigningOutAll(false)
    }
  }

  // Deleting is irreversible, so we re-verify identity with a code to the account
  // email before the worker wipes everything.
  async function handleSendDeleteCode() {
    setDeleteSending(true)
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: currentEmail,
        options: { ...await captchaOption(), shouldCreateUser: false },
      })
      if (error) throw error
      setDeleteCode('')
      setDeleteStep('code')
      showToast(t('account.delete.codeSentToast'))
    } catch (e) {
      showToast(errText(e, t('account.err.sendCode')), 'error')
    } finally {
      setDeleteSending(false)
    }
  }

  async function handleDeleteAccount() {
    setDeleting(true)
    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: currentEmail,
        token: deleteCode,
        type: 'email',
      })
      if (verifyError) { showToast(t('account.delete.badCode'), 'error'); setDeleting(false); return }

      const { data: fresh } = await supabase.auth.getSession()
      const { purgeAt } = await scheduleAccountDeletion(fresh.session?.access_token ?? session.access_token, domainChoices)
      /* Para que la pantalla de acceso diga hasta cuándo se puede volver. */
      rememberScheduledDeletion(purgeAt)
      await signOutEverywhere()
    } catch (e) {
      showToast(translateServerError(e, t('account.err.deleteAccount')), 'error')
      setDeleting(false)
      setConfirmOpen(false)
    }
  }

  return (
    <div className="dash-dashboard-view dash-settings-view">
      <div className="dash-settings-layout">
        <header className="dash-settings-intro">
          <div className="dash-header" style={{ margin: 0 }}>
            <h1>{t('account.title')}</h1>
            <p>{t('account.intro')}</p>
          </div>
          <PendingChangesActions
            dirty={settingsChanged}
            /* «Enviando…» y no «Guardando…» cuando lo que sale es el código de
               verificación del correo nuevo: es lo que está pasando de verdad. */
            saving={saving || emailLoading === 'start'}
            savingLabel={emailLoading === 'start' ? 'Enviando…' : 'Guardando…'}
            disabled={uploading || emailLoading !== null}
            onDiscard={handleDiscardSettings}
            onSave={() => { void handleSaveSettings() }}
          />
        </header>

        <div className="dash-settings-content">
          <div className="dash-account-grid">
            {/* ── Perfil ─────────────────────────────────────────────── */}
        <section className="dash-settings-card dash-settings-profile-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconUser size={18} /></span>
            <div className="dash-settings-card-head-copy"><h3>{t('account.profile.title')}</h3><p>{t('account.profile.intro')}</p></div>
          </div>

          <div className="dash-avatar-row">
            <span className="dash-avatar-lg" aria-hidden={!avatar}>
              <ProfileAvatarContent name={fallbackName} src={avatar} imageAlt={t('account.profile.photoAlt')} />
            </span>
            <div className="dash-avatar-actions">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleAvatarFile(f); e.target.value = '' }}
              />
              <button className="dash-secondary-btn" type="button" disabled={uploading} onClick={() => fileRef.current?.click()}>
                {uploading ? <><IconLoader2 size={14} className="dash-spin" /> {t('account.profile.uploading')}</> : <><IconCamera size={14} /> {t('account.profile.changePhoto')}</>}
              </button>
              {avatar ? <button className="dash-ghost-btn" type="button" onClick={() => setAvatar('')}>{t('account.profile.remove')}</button> : null}
              <p className="dash-field-hint">{t('account.profile.photoHint')}</p>
            </div>
          </div>

          <label className="dash-field">
            <span>{t('account.profile.name')}</span>
            <input type="text" value={name} placeholder={t('account.profile.namePlaceholder')} onChange={(e) => setName(e.target.value)} />
          </label>

        </section>

        {/* ── Idioma del panel ───────────────────────────────────────
            Aquí y no en los ajustes de un proyecto: es una preferencia de la
            PERSONA y vale en todos sus sitios. El idioma con el que cada
            proyecto habla a SUS clientes se elige en su Región, que es otra
            cosa — y por eso lo dice el texto de abajo. */}
        <section className="dash-settings-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconLanguage size={18} /></span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('account.language.title')}</h3>
              <p>{t('account.language.intro')}</p>
            </div>
          </div>
          <label className="dash-field">
            <span>{t('language.label')}</span>
            <select value={language} onChange={(event) => setLanguage(event.target.value as Language)}>
              {LANGUAGES.map((option) => (
                <option key={option} value={option}>{t(`language.${option}`)}</option>
              ))}
            </select>
            {/* «Región» es el botón que lleva allí, no una palabra suelta: la
                frase manda a otro sitio, así que el sitio se abre desde ella.
                El hueco va dentro de la cadena porque en inglés la palabra no
                cae donde cae en español. Sin proyecto abierto no hay Región a
                la que ir y se queda en texto. */}
            <p className="dash-field-hint">
              {t('account.language.notProject', { region: '\uE000' })
                .split(/([\uE000])/)
                .map((chunk, index) => (chunk === '\uE000'
                  ? (onOpenRegion
                      ? <button key={index} type="button" className="dash-inline-link" onClick={onOpenRegion}>{t('section.region')}</button>
                      : <span key={index}>{t('section.region')}</span>)
                  : chunk))}
            </p>
          </label>
        </section>

        {/* ── Correo electrónico ─────────────────────────────────── */}
        <section className="dash-settings-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconMail size={18} /></span>
            <div className="dash-settings-card-head-copy"><h3>{t('account.email.title')}</h3><p>{t('account.email.current')} <strong style={{ color: 'var(--dash-text)' }}>{currentEmail}</strong></p></div>
          </div>

          {!hasEmailLogin ? (
            <p className="dash-field-hint" style={{ margin: 0 }}>
              {t('account.email.external')}
            </p>
          ) : emailStep === 'verify-current' ? (
            <>
              <span className="dash-wizard-step">{t('account.email.step1')}</span>
              <div className="dash-inline-msg is-pending">
                <IconMailCheck size={14} />
                <span>{t('account.email.codeSentCurrent', { email: '\u0000' })
                    .split('\u0000')
                    .flatMap((chunk, index) => (index === 0
                      ? [chunk]
                      : [<strong key="v">{currentEmail}</strong>, chunk]))}</span>
              </div>
              <label className="dash-field">
                <span>{t('account.email.codeCurrent')}</span>
                <CodeInput
                  className="login-code-boxes--compact"
                  value={currentCode}
                  onChange={setCurrentCode}
                  onComplete={(token) => { void handleVerifyCurrent(token) }}
                  disabled={emailLoading === 'current'}
                />
              </label>
              <div className="dash-settings-card-foot dash-settings-card-foot--wizard">
                <button className="dash-ghost-btn" type="button" disabled={emailLoading !== null} onClick={backToForm}>
                  {t('account.email.back')}
                </button>
                <button className="dash-ghost-btn" type="button" disabled={emailLoading !== null} onClick={() => void handleResendCurrent()}>
                  {emailLoading === 'resend' ? t('account.resending') : t('account.resendCode')}
                </button>
                <button className="dash-primary-btn dash-new-project-btn" type="button" disabled={emailLoading !== null || currentCode.length < CODE_LENGTH} onClick={() => void handleVerifyCurrent(currentCode)}>
                  {emailLoading === 'current' ? <><IconLoader2 size={14} className="dash-spin" /> {t('account.email.verifying')}</> : 'Continuar'}
                </button>
              </div>
            </>
          ) : emailStep === 'verify-new' ? (
            <>
              <span className="dash-wizard-step">{t('account.email.step2')}</span>
              <div className="dash-inline-msg is-pending">
                <IconMailCheck size={14} />
                <span>{t('account.email.codeSentNew', { email: '\u0000' })
                    .split('\u0000')
                    .flatMap((chunk, index) => (index === 0
                      ? [chunk]
                      : [<strong key="v">{newEmail.trim()}</strong>, chunk]))}</span>
              </div>
              <label className="dash-field">
                <span>{t('account.email.codeNew')}</span>
                <CodeInput
                  className="login-code-boxes--compact"
                  value={newCode}
                  onChange={setNewCode}
                  onComplete={(token) => { void handleVerifyNew(token) }}
                  disabled={emailLoading === 'new'}
                />
              </label>
              <div className="dash-settings-card-foot dash-settings-card-foot--wizard">
                <button className="dash-ghost-btn" type="button" disabled={emailLoading !== null} onClick={backToForm}>
                  {t('account.email.back')}
                </button>
                <button className="dash-ghost-btn" type="button" disabled={emailLoading !== null} onClick={() => void handleResendNew()}>
                  {emailLoading === 'resend' ? t('account.resending') : t('account.resendCode')}
                </button>
                <button className="dash-primary-btn dash-new-project-btn" type="button" disabled={emailLoading !== null || newCode.length < CODE_LENGTH} onClick={() => void handleVerifyNew(newCode)}>
                  {emailLoading === 'new' ? <><IconLoader2 size={14} className="dash-spin" /> {t('account.email.confirming')}</> : 'Confirmar cambio'}
                </button>
              </div>
            </>
          ) : (
            <>
              {emailResume === 'verify-current' ? (
                <div className="dash-inline-msg is-pending dash-inline-msg--center">
                  <IconMailCheck size={14} />
                  <span>{t('account.email.inProgress', { email: '\u0000' })
                    .split('\u0000')
                    .flatMap((chunk, index) => (index === 0
                      ? [chunk]
                      : [<strong key="v">{currentEmail}</strong>, chunk]))}</span>
                  <div className="dash-inline-msg-actions">
                    <button className="dash-ghost-btn" type="button" onClick={discardEmailChange}>
                      {t('account.email.discard')}
                    </button>
                    <button className="dash-inline-cta" type="button" onClick={() => setEmailStep('verify-current')}>
                      {t('account.email.enterCode')}
                    </button>
                  </div>
                </div>
              ) : (!emailDismissed && (emailResume === 'verify-new' || pendingEmail)) ? (
                <div className="dash-inline-msg is-pending dash-inline-msg--center">
                  <IconMailCheck size={14} />
                  <span>{t('account.email.pendingChange', { email: '\u0000' })
                    .split('\u0000')
                    .flatMap((chunk, index) => (index === 0
                      ? [chunk]
                      : [<strong key="v">{pendingEmail || newEmail.trim()}</strong>, chunk]))}</span>
                  <div className="dash-inline-msg-actions">
                    <button className="dash-ghost-btn" type="button" onClick={discardEmailChange}>
                      {t('account.email.discard')}
                    </button>
                    <button className="dash-inline-cta" type="button" onClick={() => { if (pendingEmail) setNewEmail(pendingEmail); setEmailStep('verify-new') }}>
                      {t('account.email.enterCode')}
                    </button>
                  </div>
                </div>
              ) : null}
              <label className="dash-field">
                <span>{t('account.email.newEmail')}</span>
                <input type="email" value={newEmail} autoComplete="email" placeholder={t('account.email.newPlaceholder')} onChange={(e) => setNewEmail(e.target.value)} />
                {emailSuggestion ? (
                  <button type="button" className="dash-email-suggest" onClick={() => setNewEmail(emailSuggestion)}>
                    {t('account.email.didYouMean', { suggestion: '\u0000' })
                    .split('\u0000')
                    .flatMap((chunk, index) => (index === 0
                      ? [chunk]
                      : [<strong key="v">{emailSuggestion}</strong>, chunk]))}
                  </button>
                ) : null}
              </label>
            </>
          )}
        </section>

        {/* ── Proveedores conectados ─────────────────────────────── */}
        <section className="dash-settings-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconLink size={18} /></span>
            <div className="dash-settings-card-head-copy"><h3>{t('account.providers.title')}</h3><p>{t('account.providers.intro')}</p></div>
          </div>

          <ul className="dash-provider-list">
            {hasEmailLogin ? (
              <li className="dash-provider-row">
                <span className="dash-provider-left">
                  <span className="dash-provider-icon dash-widget-glass-icon dash-sidebar-glass-icon dash-list-glass-icon is-fog"><IconMail size={15} /></span>
                  <span className="dash-provider-name">{t('account.email.title')}</span>
                </span>
                <span className="dash-provider-tag">{t('account.providers.connected')}</span>
              </li>
            ) : null}
            <li className="dash-provider-row">
              <span className="dash-provider-left">
                <span className="dash-provider-icon dash-widget-glass-icon dash-sidebar-glass-icon dash-list-glass-icon is-fog"><GoogleIcon size={17} /></span>
                <span className="dash-provider-name">{/* Nombre propio: igual en los dos idiomas. */}{'Google'}</span>
              </span>
              {googleIdentity ? (
                <button className="dash-secondary-btn" type="button" disabled={identities.length <= 1} onClick={() => void handleUnlink(googleIdentity)}>
                  <IconUnlink size={14} /> {t('account.providers.disconnect')}
                </button>
              ) : (
                <button className="dash-secondary-btn" type="button" onClick={() => void handleLinkGoogle()}>
                  <IconLink size={14} /> {t('account.providers.connect')}
                </button>
              )}
            </li>
          </ul>
        </section>

        {/* ── Sesiones ───────────────────────────────────────────── */}
        <section className="dash-settings-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconDeviceDesktop size={18} /></span>
            <div className="dash-settings-card-head-copy"><h3>{t('account.sessions.title')}</h3><p>{t('account.sessions.intro')}</p></div>
          </div>

          <ul className="dash-session-list">
            {sessionsLoading ? (
              <li className="dash-session-empty"><IconLoader2 size={14} className="dash-spin" /> {t('account.sessions.loading')}</li>
            ) : sessions.length === 0 ? (
              <li className="dash-session-empty">{t('account.sessions.failed')}</li>
            ) : orderedSessions.map((s) => (
              <li className="dash-session-row" key={s.id}>
                <span className="dash-session-icon dash-widget-glass-icon dash-sidebar-glass-icon dash-list-glass-icon is-fog"><IconDeviceDesktop size={15} /></span>
                <span className="dash-session-info">
                  <span className="dash-session-name">
                    <span>{deviceLabel(s.userAgent)}</span>
                    {s.current ? <em className="dash-session-current">{t('account.sessions.current')}</em> : null}
                  </span>
                  <span className="dash-session-meta">
                    <span>{t('account.sessions.startedAt', { date: fmtDate(s.createdAt) })}</span>
                    <span className="dash-session-location">{sessionLocation(s)}</span>
                  </span>
                </span>
                {s.current ? (
                  <button className="dash-secondary-btn" type="button" onClick={() => void signOutEverywhere().catch(() => {})}>
                    <IconLogout size={14} /> {t('account.sessions.signOutCurrent')}
                  </button>
                ) : (
                  <button className="dash-secondary-btn" type="button" disabled={revokingId === s.id} onClick={() => void handleRevokeSession(s.id)}>
                    {revokingId === s.id ? <><IconLoader2 size={14} className="dash-spin" /> {t('account.sessions.signingOut')}</> : <><IconLogout size={14} /> {t('account.sessions.signOut')}</>}
                  </button>
                )}
              </li>
            ))}
          </ul>

          <div className="dash-settings-card-foot" style={{ gap: 10, flexWrap: 'wrap' }}>
            <button className="dash-secondary-btn" type="button" disabled={signingOutAll} onClick={() => void handleSignOutAll()}>
              {signingOutAll ? <><IconLoader2 size={14} className="dash-spin" /> {t('account.sessions.signingOut')}</> : <><IconLogout size={14} /> {t('account.sessions.signOutAll')}</>}
            </button>
          </div>
        </section>

        {/* ── Zona de peligro ────────────────────────────────────── */}
        <section className="dash-settings-card dash-danger-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconTrash size={18} /></span>
            <div className="dash-settings-card-head-copy"><h3>{t('account.delete.title')}</h3><p>{t('account.delete.graceIntro', { days: preflight?.graceDays ?? 30 })}</p></div>
          </div>

          {preflight?.purchasePending ? (
            <p className="dash-inline-msg is-err">{t('error.account.purchasePending')}</p>
          ) : null}

          {/* Los dominios registrados aquí: uno por fila, con su decisión. */}
          {preflight?.domains.length ? (
            <div className="dash-delete-domains">
              <span className="dash-delete-section-label">{t('account.delete.domainsTitle')}</span>
              <p className="dash-field-hint">{t('account.delete.domainsHint')}</p>
              {preflight.domains.map((domain) => {
                const choice = domainChoices[domain.id]
                return (
                  <div className="dash-delete-domain" key={domain.id}>
                    <div className="dash-delete-domain-head">
                      <strong>{domain.hostname}</strong>
                      {domain.expiresAt ? <small>{t('account.delete.domainExpires', { date: fmt.date(domain.expiresAt) })}</small> : null}
                    </div>
                    <div className="dash-delete-domain-choices" role="radiogroup" aria-label={domain.hostname}>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={choice === 'transfer'}
                        className={`dash-delete-choice${choice === 'transfer' ? ' is-active' : ''}`}
                        disabled={!domain.transferable}
                        onClick={() => setDomainChoices((current) => ({ ...current, [domain.id]: 'transfer' }))}
                      >
                        <strong>{t('account.delete.domainTransfer')}</strong>
                        <span>{domain.transferable ? t('account.delete.domainTransferHint') : t('account.delete.domainLocked')}</span>
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={choice === 'expire'}
                        className={`dash-delete-choice${choice === 'expire' ? ' is-active' : ''}`}
                        onClick={() => setDomainChoices((current) => ({ ...current, [domain.id]: 'expire' }))}
                      >
                        <strong>{t('account.delete.domainExpire')}</strong>
                        <span>{t('account.delete.domainExpireHint')}</span>
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : null}

          {/* Lo que deja de funcionar para otros: sólo lo que de verdad hay. */}
          {preflight && (preflight.teamProjects.length || preflight.futureBookings || preflight.customerSubscriptions || preflight.openDisputes) ? (
            <ul className="dash-delete-notices">
              {preflight.teamProjects.length ? (
                <li>{t('account.delete.noticeTeam', { names: preflight.teamProjects.map((project) => project.name || t('account.projectUnnamed')).join(', ') })}</li>
              ) : null}
              {preflight.futureBookings ? <li>{t(`account.delete.noticeBookings.${preflight.futureBookings === 1 ? 'one' : 'many'}`, { count: preflight.futureBookings })}</li> : null}
              {preflight.customerSubscriptions ? <li>{t(`account.delete.noticeSubscriptions.${preflight.customerSubscriptions === 1 ? 'one' : 'many'}`, { count: preflight.customerSubscriptions })}</li> : null}
              {preflight.openDisputes ? <li>{t(`account.delete.noticeDisputes.${preflight.openDisputes === 1 ? 'one' : 'many'}`, { count: preflight.openDisputes })}</li> : null}
            </ul>
          ) : null}

          <label className="dash-field">
            <span>{t('account.delete.typeToConfirm', { word: '\u0000' })
                    .split('\u0000')
                    .flatMap((chunk, index) => (index === 0
                      ? [chunk]
                      : [<strong key="v">{t('account.delete.word')}</strong>, chunk]))}</span>
            <input type="text" value={deleteText} placeholder={t('account.delete.word')} onChange={(e) => setDeleteText(e.target.value)} />
          </label>

          {deleteStep === 'code' ? (
            <>
              <div className="dash-inline-msg is-pending">
                <IconMailCheck size={14} />
                <span>{t('account.delete.codeSent', { email: '\u0000' })
                    .split('\u0000')
                    .flatMap((chunk, index) => (index === 0
                      ? [chunk]
                      : [<strong key="v">{currentEmail}</strong>, chunk]))}</span>
              </div>
              <label className="dash-field">
                <span>{t('account.delete.code')}</span>
                <CodeInput className="login-code-boxes--compact" value={deleteCode} onChange={setDeleteCode} onComplete={() => {}} disabled={deleting} />
              </label>
              <div className="dash-settings-card-foot" style={{ gap: 10, flexWrap: 'wrap' }}>
                <button className="dash-danger-btn" type="button" disabled={deleting || deleteCode.length < CODE_LENGTH} onClick={() => setConfirmOpen(true)}>
                  <IconTrash size={14} /> {t('account.delete.button')}
                </button>
                <button className="dash-ghost-btn" type="button" disabled={deleteSending} onClick={() => void handleSendDeleteCode()}>
                  {deleteSending ? t('account.resending') : t('account.resendCode')}
                </button>
              </div>
            </>
          ) : (
            <div className="dash-settings-card-foot">
              <button className="dash-danger-btn" type="button" disabled={deleteSending || !deleteReady} onClick={() => void handleSendDeleteCode()}>
                {deleteSending ? <><IconLoader2 size={14} className="dash-spin" /> {t('account.delete.sending')}</> : <><IconTrash size={14} /> {t('account.delete.sendCode')}</>}
              </button>
            </div>
          )}
        </section>
      </div>
        </div>
      </div>

      {confirmOpen ? (
        <div
          className="dash-modal-backdrop"
          role="presentation"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !deleting) setConfirmOpen(false) }}
        >
          <section className="dash-modal" role="dialog" aria-modal="true" aria-label={t('account.delete.dialogAria')}>
            <header className="dash-modal-header">
              <div className="dash-modal-icon dash-modal-icon--danger"><IconTrash size={20} /></div>
              <h2>{t('account.delete.dialogTitle')}</h2>
              <p>{t('account.delete.dialogGrace', { days: preflight?.graceDays ?? 30 })}</p>
            </header>
            <div className="dash-modal-body">
              {projects.length > 0 ? (
                <ul className="dash-delete-list">
                  {projects.map((p) => (
                    <li key={p.id}><IconTrash size={13} /> {p.name?.trim() || t('account.projectUnnamed')}</li>
                  ))}
                </ul>
              ) : (
                <p className="dash-field-hint" style={{ margin: 0 }}>{t('account.delete.noProjects')}</p>
              )}
            </div>
            <footer>
              <button className="dash-secondary-btn" type="button" disabled={deleting} onClick={() => setConfirmOpen(false)}>
                {t('account.cancel')}
              </button>
              <button className="dash-danger-btn" type="button" disabled={deleting} onClick={() => void handleDeleteAccount()}>
                {deleting ? <><IconLoader2 size={14} className="dash-spin" /> {t('account.delete.deleting')}</> : <><IconTrash size={14} /> {t('account.delete.confirmAll')}</>}
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </div>
  )
}
